import type { CSSProperties } from 'react';
import { Link, Navigate, NavLink, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth.js';
import { RepoOverview } from './routes/repo-overview.js';
import { VelocityScorecard } from './routes/velocity-scorecard.js';
import { CostView } from './routes/cost.js';
import { RepoDashboard } from './routes/repo-dashboard.js';
import { PrDetail } from './routes/pr-detail.js';
import { WeeklyDigest } from './routes/weekly-digest.js';
import { PolicyVersions } from './routes/policy-versions.js';
import { Suppressions } from './routes/suppressions.js';
import { DvConfig } from './routes/dv-config.js';
import { AboutReactions } from './routes/about-reactions.js';
import { Incidents } from './routes/incidents.js';
import { Calibration } from './routes/calibration.js';
import { Slicing } from './routes/slicing.js';
import { HelpPage } from './routes/help.js';
import { ConceptHelpIcon } from './components/ConceptHelpIcon.js';
import { RequireRole } from './components/RequireRole.js';
import { RoleBadge } from './components/RoleBadge.js';
import { Users } from './routes/users.js';
import { ConfigLayout } from './routes/config-layout.js';
import { ConfigAudit } from './routes/config-audit.js';

const navLinkStyle = ({ isActive }: { isActive: boolean }): CSSProperties => ({
  color: isActive ? 'var(--loop-brand-600)' : 'var(--loop-neutral-700)',
  fontWeight: isActive ? 528 : 400,
  textDecoration: 'none',
  fontSize: '0.875rem',
  padding: '4px 8px',
  borderRadius: 'var(--loop-radius-xs)'
});

export function App() {
  const { idToken, email, role, signIn, signOut, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--loop-neutral-100)' }}>
        <div style={{ color: 'var(--loop-neutral-700)' }}>Loading...</div>
      </div>
    );
  }

  if (!idToken) {
    // Public /about/* explainer routes are accessible without sign-in.
    // The pages contain only public copy — no API calls, no PII, no
    // links into authenticated portal routes. The footer hint embedded
    // in every Diff Vader PR review comment links to /about/reactions,
    // and devs without portal access need to be able to read it.
    if (window.location.pathname.startsWith('/about/')) {
      return (
        <Routes>
          <Route path="/about/reactions" element={<AboutReactions />} />
        </Routes>
      );
    }
    return (
      <div style={{ minHeight: '100vh', background: 'var(--loop-neutral-100)' }}>
        <div className="signin-card">
          <h1>Death Star</h1>
          <p className="signin-subtitle">Diff Vader observability portal</p>
          <button onClick={signIn} className="google-btn">
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--loop-neutral-100)' }}>
      <header>
        <div className="header-brand">
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 'var(--loop-space-100)', textDecoration: 'none' }}>
            <img src="/favicon.svg" alt="" className="header-logo" />
            <h1>Death Star</h1>
          </Link>
          <nav style={{ display: 'flex', gap: 'var(--loop-space-050)', marginLeft: 'var(--loop-space-300)' }}>
            <NavLink to="/scorecard" style={navLinkStyle}>Scorecard</NavLink>
            <NavLink to="/slicing" style={navLinkStyle}>Slicing</NavLink>
            <NavLink to="/repos" style={navLinkStyle}>Repos</NavLink>
            <NavLink to="/incidents" style={navLinkStyle}>Incidents</NavLink>
            <NavLink to="/digest" style={navLinkStyle}>Digest</NavLink>
            <NavLink to="/suppressions" style={navLinkStyle}>Suppressions</NavLink>
            <NavLink to="/cost" style={navLinkStyle}>Cost</NavLink>
            <NavLink to="/calibration" style={navLinkStyle}>Calibration</NavLink>
            <NavLink to="/policy-versions" style={navLinkStyle}>Policies</NavLink>
            <NavLink to="/config" style={navLinkStyle}>Configuration</NavLink>
          </nav>
        </div>
        <div id="who">
          <RoleBadge role={role} />
          <span className="who-email">{email}</span>
          <ConceptHelpIcon label="Help — How Diff Vader & Death Star fit together" />
          <button onClick={signOut} className="btn-secondary signout-btn">
            <span aria-hidden="true">↪</span> Sign out
          </button>
        </div>
      </header>
      <main>
        <Routes>
          <Route path="/" element={<Navigate to="/scorecard" replace />} />
          <Route path="/repos" element={<RepoOverview />} />
          <Route path="/repos/:org/:repo" element={<RepoDashboard />} />
          <Route path="/repos/:org/:repo/pr/:pr" element={<PrDetail />} />
          <Route path="/scorecard" element={<VelocityScorecard />} />
          <Route path="/incidents" element={<Incidents />} />
          <Route path="/cost" element={<CostView />} />
          <Route path="/digest" element={<WeeklyDigest />} />
          <Route path="/policy-versions" element={<PolicyVersions />} />
          <Route path="/suppressions" element={<Suppressions />} />
          <Route path="/calibration" element={<Calibration />} />
          <Route path="/slicing" element={<Slicing />} />
          <Route
            path="/config"
            element={<RequireRole min="superAdmin"><ConfigLayout /></RequireRole>}
          >
            <Route index element={<DvConfig />} />
            <Route path="users" element={<Users />} />
            <Route path="audit" element={<ConfigAudit />} />
          </Route>
          <Route path="/users" element={<Navigate to="/config/users" replace />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/about/reactions" element={<AboutReactions />} />
        </Routes>
      </main>
    </div>
  );
}
